import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { glossary } from '@sorare/core/src/lib/glossary';

import { SearchTableLastCell } from '#src/components/searchPlayerTable/SearchTable/ui.tsx';
import { StartingPrice } from '#src/components/searchPlayerTable/StartingPrice/index.tsx';
import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type { StartingPriceCell_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: StartingPriceCell_anyPlayer;
  rarity: Rarity;
  onListingsClick: () => void;
};

export const StartingPriceCell = ({
  anyPlayer,
  rarity,
  onListingsClick,
}: Props) => {
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { lowestPriceAnyCard } = anyPlayer;
  const offer = useGetTokenSingleSaleDetails(lowestPriceAnyCard);
  const primaryBuy = useIsPrimaryBuyLive(lowestPriceAnyCard);
  const monetaryAmount = (primaryBuy || offer)?.price;
  return (
    <SearchTableLastCell isLaptopOrAbove={isLaptopOrAbove}>
      {!!monetaryAmount && (
        <StartingPrice
          small={!isLaptopOrAbove}
          column={!isLaptopOrAbove}
          monetaryAmount={monetaryAmount}
        />
      )}

      {!!isLaptopOrAbove && (
        <Horizontal>
          <Button
            color="secondary"
            size="small"
            startIcon={<ScarcityIcon rarity={rarity} />}
            onClick={onListingsClick}
          >
            <FormattedMessage {...glossary.listings} />
          </Button>
        </Horizontal>
      )}
    </SearchTableLastCell>
  );
};

StartingPriceCell.fragments = {
  anyPlayer: gql`
    fragment StartingPriceCell_anyPlayer on AnyPlayerInterface {
      slug
      lowestPriceAnyCard(rarity: $rarity, inSeason: $inSeason) {
        slug
        rarityTyped
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
    }
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
  ` as TypedDocumentNode<StartingPriceCell_anyPlayer>,
};
